import React, { ReactElement, useCallback, useState } from 'react';
import jamespot, { jFileLittle } from 'jamespot-user-api';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';
import { strToTimestamp } from 'utils/utils.date';
import { JRCModalButtonType } from '../../JRCModal/JRCModalContent';
import { JRCCropImage } from '../../JRCCropImage/JRCCropImage';
import { JRCModal } from '../../JRCModal/templates/JRCModal';
import { JRCInputImage } from '../../Form/Input/JRCInputImage/JRCInputImage';

const InputImageWrapper = styled.div`
    width: 50%;
    margin: 0 auto 20px auto;
`;

type FileFormValues = { file: jFileLittle };

type EditorImageUploadProps = {
    token: string;
    initialFile?: jFileLittle;
    children(open: () => void, openCrop: () => void): ReactElement<HTMLElement>;
    onUploadSuccess: (file: jFileLittle) => void;
};

export const EditorImageUpload = ({ token, children, initialFile, onUploadSuccess }: EditorImageUploadProps) => {
    const intl = useIntl();
    const form = useForm<FileFormValues>();
    const theme = useTheme();
    const [open, setOpen] = useState<boolean>(false);
    const [openCrop, setOpenCrop] = useState<boolean>(false);

    const closeModal = useCallback(() => {
        form.reset();
        setOpen(false);
    }, [form]);

    const saveImage = useCallback(() => {
        const file = form.watch('file');
        onUploadSuccess(file);
        setOpen(false);
    }, [form, onUploadSuccess]);

    const buttons: JRCModalButtonType[] = [
        {
            children: <FormattedMessage id="GLOBAL_Cancel" />,
            color: 'cancel',
            clickHandler: closeModal,
        },
        {
            children: <FormattedMessage id="GLOBAL_Save" />,
            color: 'valid',
            clickHandler: saveImage,
        },
    ];

    return (
        <>
            {children(
                () => {
                    setOpen(!open);
                },
                () => setOpenCrop(!openCrop),
            )}
            {initialFile && openCrop && (
                <>
                    <JRCCropImage
                        zIndex={theme.zIndex.tinyMCEModal}
                        open={openCrop}
                        aspectRatio={16 / 9}
                        allowCustomRatio={false}
                        src={`/imagecache/original/${initialFile.type}/${initialFile.id}.png?_=${
                            initialFile.dateModified ? strToTimestamp(initialFile.dateModified) : Date.now()
                        }`}
                        save={(cropConfig) => {
                            if (initialFile) {
                                const cropSettings = {
                                    x: cropConfig.x,
                                    y: cropConfig.y,
                                    w: cropConfig.width,
                                    h: cropConfig.height,
                                    rotation: cropConfig.rotation,
                                    killSize: true,
                                };

                                jamespot.file.update(initialFile.id, initialFile.type, cropSettings).then((res) => {
                                    setOpenCrop(false);
                                    onUploadSuccess(res.result);
                                });
                            }
                        }}
                        closeHandler={() => {
                            setOpenCrop(false);
                        }}
                    />
                </>
            )}
            <JRCModal
                open={open}
                closeHandler={closeModal}
                zIndex={theme.zIndex.tinyMCEModal}
                title={intl.formatMessage({ id: 'GLOBAL_Add_Image' })}
                buttons={buttons}
                enableClickAwayCloseModal={false}>
                <InputImageWrapper>
                    <JRCInputImage
                        zIndex={theme.zIndex.tinyMCEModal}
                        control={form.control}
                        name={'file'}
                        token={token}
                        attrName={'editorFiles'}
                        accept={'jpg,jpeg,png,gif'}
                        aspectRatio={16 / 9}
                    />
                </InputImageWrapper>
            </JRCModal>
        </>
    );
};
